/**
 * ExecutionArtifacts — the shared artifact grid for an execution.
 *
 * Both drill-down surfaces (pages/reports/RunDetail.tsx and the Test Run
 * Details modal in pages/Tests.tsx) used to carry near-identical copies of this
 * grid; they now render this component instead.
 *
 * Two behaviours the old copies lacked:
 *
 *   1. `voice_wav` rows get an inline <audio> player. Recordings have been
 *      written to Storage since the AI voice agent shipped, but nothing in the
 *      app could play them — downloading a WAV to hear a 10 s call is not a
 *      review workflow. The signed URL is fetched lazily on first play (they
 *      expire in 15 min, so eagerly minting one per row on mount would waste
 *      most of them and burn a REST call per artifact).
 *
 *   2. Artifacts registered with a `skipReason` (capture was attempted and
 *      deliberately not produced — e.g. a modem with no UAC audio path) render
 *      as a muted, non-clickable row explaining why. Previously they looked
 *      like a normal download and 410'd on click.
 */

import React, { useCallback, useRef, useState } from 'react';
import { Download, FileText, Loader2, AudioLines, Play, Ban } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { formatDuration } from '@/lib/utils';
import { fetchArtifactSignedUrl } from '@/services/reportsService';

/** The subset of ArtifactManifest the drill-down surfaces actually read. */
export interface ExecutionArtifactRow {
  id: string;
  type: string;
  filename?: string;
  sizeBytes?: number;
  contentType?: string;
  skipReason?: string;
  meta?: {
    durationMs?: number;
    sampleRateHz?: number;
    channels?: number;
    [k: string]: unknown;
  };
}

interface Props {
  artifacts: ExecutionArtifactRow[];
  executionId: string;
  organizationId: string | null | undefined;
  /** Tailwind grid classes — surfaces differ (modal is narrower than the page). */
  gridClassName?: string;
  /**
   * #252: modem-scoped artifacts are signed through a different endpoint.
   * When set, replaces the default execution-scoped fetcher.
   */
  signedUrlFetcher?: (artifactId: string) => Promise<{ signedUrl: string; filename?: string }>;
  /** Extra text appended to a row's subtitle (e.g. trigger + window). */
  extraSubtitle?: (a: ExecutionArtifactRow) => string | null;
}

export function formatBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Secondary line under the filename: type · size · duration. */
function subtitle(a: ExecutionArtifactRow): string {
  const parts: string[] = [a.type];
  if (a.sizeBytes) parts.push(formatBytes(a.sizeBytes));
  if (a.meta?.durationMs) parts.push(formatDuration(a.meta.durationMs));
  return parts.join(' · ');
}

export const ExecutionArtifacts: React.FC<Props> = ({
  artifacts,
  executionId,
  organizationId,
  gridClassName = 'grid grid-cols-1 gap-3 sm:grid-cols-2',
  signedUrlFetcher,
  extraSubtitle,
}) => {
  const toast = useToast();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const getSignedUrl = useCallback(
    async (artifactId: string) => {
      if (signedUrlFetcher) return signedUrlFetcher(artifactId);
      if (!organizationId) throw new Error('No active organization');
      return fetchArtifactSignedUrl(organizationId, executionId, artifactId);
    },
    [organizationId, executionId, signedUrlFetcher],
  );

  const handleDownload = useCallback(
    async (a: ExecutionArtifactRow) => {
      setDownloadingId(a.id);
      try {
        const signed = await getSignedUrl(a.id);
        // Same-tab anchor click avoids popup blockers — the object's
        // Content-Disposition makes the browser download rather than navigate.
        const el = document.createElement('a');
        el.href = signed.signedUrl;
        el.download = signed.filename || a.filename || a.id;
        el.rel = 'noopener noreferrer';
        document.body.appendChild(el);
        el.click();
        setTimeout(() => el.remove(), 100);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to fetch download link');
      } finally {
        setDownloadingId(null);
      }
    },
    [getSignedUrl, toast],
  );

  if (!artifacts.length) return null;

  return (
    <div className={gridClassName}>
      {artifacts.map((a) =>
        a.skipReason ? (
          <SkippedRow key={a.id} artifact={a} />
        ) : a.type === 'voice_wav' ? (
          <VoiceRow
            key={a.id}
            artifact={a}
            getSignedUrl={getSignedUrl}
            onDownload={handleDownload}
            downloading={downloadingId === a.id}
          />
        ) : (
          <button
            key={a.id}
            onClick={() => handleDownload(a)}
            disabled={downloadingId === a.id}
            className="flex items-center justify-between gap-2 rounded border border-border p-3 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            <div className="flex min-w-0 items-center gap-2">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{a.filename || a.id}</div>
                <div className="text-xs text-muted-foreground">{subtitle(a)}{extraSubtitle?.(a) ? ` · ${extraSubtitle(a)}` : ''}</div>
              </div>
            </div>
            {downloadingId === a.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </button>
        ),
      )}
    </div>
  );
};

/** A capture that was attempted and deliberately not produced. */
const SkippedRow: React.FC<{ artifact: ExecutionArtifactRow }> = ({ artifact }) => (
  <div className="flex items-start gap-2 rounded border border-dashed border-border p-3 opacity-70">
    <Ban className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-muted-foreground">
        {artifact.filename || artifact.type}
      </div>
      <div className="text-xs text-muted-foreground">Not captured — {artifact.skipReason}</div>
    </div>
  </div>
);

/**
 * Inline player for a call recording. The <audio> element is only given a src
 * after the user asks to play, because signed URLs are short-lived (15 min).
 * If playback later fails because the URL aged out, we re-mint once and retry.
 */
const VoiceRow: React.FC<{
  artifact: ExecutionArtifactRow;
  getSignedUrl: (id: string) => Promise<{ signedUrl: string }>;
  onDownload: (a: ExecutionArtifactRow) => void;
  downloading: boolean;
}> = ({ artifact, getSignedUrl, onDownload, downloading }) => {
  const toast = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Guards the expiry re-mint below. Without a hard limit, a manifest whose
  // object is permanently unfetchable (retention deleted the blob, corrupt
  // bytes) turns "re-mint and retry" into a hot loop: every new src remounts
  // the element, which errors, which re-mints — several signed-URL calls a
  // second, silently, for as long as the tab is open.
  const retriedRef = useRef(false);

  const load = useCallback(
    async (autoplay: boolean) => {
      setLoading(true);
      try {
        const signed = await getSignedUrl(artifact.id);
        setSrc(signed.signedUrl);
        if (autoplay) {
          // Wait for React to attach the new src before asking to play.
          requestAnimationFrame(() => {
            audioRef.current?.play().catch(() => {
              /* autoplay policy — the user can hit the native play button */
            });
          });
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to load recording');
      } finally {
        setLoading(false);
      }
    },
    [artifact.id, getSignedUrl, toast],
  );

  return (
    <div className="flex flex-col gap-2 rounded border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <AudioLines className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {artifact.filename || 'Call recording'}
            </div>
            <div className="text-xs text-muted-foreground">{subtitle(artifact)}</div>
          </div>
        </div>
        <button
          onClick={() => onDownload(artifact)}
          disabled={downloading}
          title="Download recording"
          className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </button>
      </div>

      {src ? (
        <audio
          ref={audioRef}
          src={src}
          controls
          preload="metadata"
          className="w-full"
          // A signed URL that expired between load and play surfaces here.
          // Re-mint EXACTLY once; a second failure is the object itself, not
          // the URL, so stop rather than loop.
          onError={() => {
            if (retriedRef.current) {
              setSrc(null);
              setFailed(true);
              return;
            }
            retriedRef.current = true;
            setSrc(null);
            void load(true);
          }}
        />
      ) : failed ? (
        <div className="text-xs text-muted-foreground">
          This recording could not be played — it may have passed the artifact
          retention window. Try downloading it.
        </div>
      ) : (
        <button
          onClick={() => load(true)}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {loading ? 'Loading…' : 'Play recording'}
        </button>
      )}
    </div>
  );
};

export default ExecutionArtifacts;
